import { PayloadAction } from '@reduxjs/toolkit';

import { Condition, ConditionAddress, DesignState } from 'editor/src/store/design/types';

import { captureException } from 'editor/src/logger';

import getConditionChildrenKey from './getConditionChildrenKey';
import getConditionParentKeys from './getConditionParentKeys';

const duplicateConditionReducer = (
  state: DesignState,
  action: PayloadAction<{
    address: ConditionAddress;
    newConditionId: string;
    newName: string;
    /** Old option id -> freshly generated id, in the source's option order. */
    optionIdMap: { oldId: string; newId: string }[];
  }>,
) => {
  const { address, newConditionId, newName, optionIdMap } = action.payload;
  const { spreadIndex, conditionId } = address;
  const conditionGroup = state.designData?.spreads[spreadIndex]?.conditionGroup;
  if (!conditionGroup) {
    return;
  }

  const source = conditionGroup.conditions[conditionId];
  if (!source) {
    return;
  }

  if (conditionGroup.conditions[newConditionId]) {
    // Same guard as createEmptyConditionReducer: a colliding id would destroy
    // an existing condition and its children buckets.
    captureException('Condition id collision on duplicate', { extra: { newConditionId } });
    return;
  }

  // Copy option labels/images only — nothing LINKED to the source options
  // (elements, sub-conditions, text variants) is copied, per spec: the
  // duplicate starts with empty children buckets for every option.
  const options = optionIdMap.map(({ oldId, newId }) => {
    const sourceOption = source.options.find((option) => option.id === oldId)!;
    return { id: newId, label: sourceOption.label, imageId: sourceOption.imageId };
  });

  const activeIndex = source.options.findIndex((option) => option.id === source.activeOptionId);
  const activeOptionId = optionIdMap[activeIndex]?.newId ?? options[0].id;

  const duplicate: Condition = {
    id: newConditionId,
    parent: source.parent,
    name: newName,
    description: source.description,
    showOnAllSpreads: source.showOnAllSpreads,
    displayOption: source.displayOption,
    options,
    activeOptionId,
  };

  conditionGroup.conditions[newConditionId] = duplicate;
  options.forEach((option) => {
    conditionGroup.children[getConditionChildrenKey(newConditionId, option.id)] = [];
  });

  // Next to the source in EVERY bucket it sits in: a sub-condition shown under
  // several parent options is listed in each of their buckets (see
  // getConditionParentKeys), and the copy shares the source's parent.
  getConditionParentKeys(conditionGroup, conditionId).forEach((parentKey) => {
    const siblings = conditionGroup.children[parentKey];
    if (!siblings) {
      return;
    }
    const sourceIndex = siblings.findIndex((child) => child.type === 'condition' && child.id === conditionId);
    const insertAt = sourceIndex === -1 ? siblings.length : sourceIndex + 1;
    siblings.splice(insertAt, 0, { type: 'condition', id: newConditionId });
  });
};

export default duplicateConditionReducer;
